import { Box, Button, Divider, Slider, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import * as React from "react";
import { Card } from "../../general/card";
import useBackend, { useUserPrivilegeKeyPermissionsMap } from "../../../hooks/use_backend";
import { ManualOverrideDialog } from "./override_dialog";
import {
  getAdminLog,
  sendManualOverrideClearAdmin,
  sendManualOverrideCommandAdmin,
} from "../../../scripts/api_v2/admin_client";
import { isComponentDisabledForUpdates } from "../../../pages/login/login_utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import Snackbar from "../../../scripts/snackbar";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";

function valueLabelFormat(v) {
  return v > 0 ? `Charge ${100 * v}%` : v == 0 ? `Self-Consumption` : `Discharge ${-100 * v}%`;
}

function feedInLabelFormat(v) {
  return v ? "Feed-In Limited" : "No Feed-In Limit";
}

export default function OverrideTab({ required_privilege }) {
  const [action, setAction] = React.useState(0);
  const [feedInLimit, setFeedInLimit] = React.useState(false);
  const [quarters, setQuarters] = React.useState(2);
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const [open, setOpen] = React.useState(false);

  const backend = useBackend();
  let {
    data: result,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.ADMIN_LOG, backend.fetch],
    queryFn: () => getAdminLog(backend.fetch),
    refetchInterval: 30000,
  });

  const handleSend = () => {
    sendOverrideMutation({ isClear: true, action: action, feedInLimit: feedInLimit, quarters: quarters });
  };
  const handleClear = () => {
    sendOverrideMutation({ isClear: true, action: null, feedInLimit: null, quarters: null });
  };

  const { mutate: sendOverrideMutation, isPending: isMutating } = useMutation({
    mutationFn: async ({ isClear, action, feedInLimit, quarters }) => {
      if (isClear) {
        return await sendManualOverrideClearAdmin(backend.fetch);
      } else {
        return await sendManualOverrideCommandAdmin(backend.fetch, action, feedInLimit, quarters);
      }
    },
    onSuccess: (data) => {
      refetch();
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not execute override " + err);
    },
  });

  return (
    <>
      <Card sx={{ m: 2 }} display={"flex"} alignItems={"center"} flexDirection="column" title="Override">
        <Box width={400}>
          <Divider sx={{ my: 3 }} />

          <Typography variant="h6">
            <b>Action:</b> {valueLabelFormat(action)}
          </Typography>
          <Slider
            value={action}
            track={false}
            getAriaValueText={valueLabelFormat}
            valueLabelFormat={valueLabelFormat}
            onChange={(e, v) => setAction(v)}
            valueLabelDisplay="auto"
            step={0.1}
            sx={{ width: 390, marginLeft: "5px" }}
            marks
            min={-1}
            max={1}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            <b>Grid feed-in limit:</b> {feedInLabelFormat(feedInLimit)}
          </Typography>
          <ToggleButtonGroup
            value={feedInLimit}
            exclusive
            fullWidth
            onChange={(v) => setFeedInLimit(v.target.value == "true")}
            aria-label="text alignment"
          >
            <ToggleButton value={false}>{feedInLabelFormat(false)}</ToggleButton>
            <ToggleButton value={true}>{feedInLabelFormat(true)}</ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6">
            <b>Kwartieren:</b> {quarters}
          </Typography>
          <Slider
            value={quarters}
            track={false}
            onChange={(e, v) => setQuarters(v)}
            valueLabelDisplay="auto"
            step={1}
            sx={{ width: 390, marginLeft: "5px" }}
            marks
            min={1}
            max={12}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ my: 3, color: "red" }}>
            {error ? <b>{error.toString()}</b> : null}
          </Typography>

          <Button
            disabled={isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey())}
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={() => setOpen(true)}
          >
            Send
          </Button>
          <Button
            disabled={isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey())}
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleClear}
          >
            Clear
          </Button>
        </Box>

        <ManualOverrideDialog open={open} setOpen={setOpen} handleSend={handleSend}>
          <Typography variant="body">
            <b>Action:</b> {valueLabelFormat(action)} <br />
            <b>Grid feed-in limit:</b> {feedInLabelFormat(feedInLimit)} <br />
            <b>Kwartieren:</b> {quarters}
          </Typography>
        </ManualOverrideDialog>
      </Card>
      <Card sx={{ m: 2 }} title="Log">
        {result &&
          result
            .map((el) =>
              el.type === "clear" ? (
                <ClearLine utctime={el.utctime} executed={el.executed} total={el.total} />
              ) : (
                <OverrideLine
                  utctime={el.utctime}
                  executed={el.executed}
                  total={el.total}
                  action={el.action}
                  gridLimit={el.grid_feed_in_limit}
                  quarters={el.quarters}
                />
              ),
            )
            .reverse()}
      </Card>
    </>
  );
}

function ClearLine({ utctime, executed, total }) {
  return (
    <Typography>
      [CLEAR] {new Date(1000 * utctime).toLocaleString()}: {executed}/{total}
    </Typography>
  );
}

function OverrideLine({ utctime, executed, total, action, gridLimit, quarters }) {
  return (
    <Typography>
      [OVERRIDE] {new Date(1000 * utctime).toLocaleString()}: {executed}/{total} action: {action}, grid feed-in limit:{" "}
      {gridLimit ? "true" : "false"}, quarters: {quarters}
    </Typography>
  );
}
